import { useEffect, useState, type ReactNode } from 'react'

/**
 * The toast store, ported from legacy `hooks/use-toast.ts`.
 *
 * A module-level store rather than context, because `toast()` is called from
 * mutation callbacks and error handlers that are nowhere near a provider — the
 * same reason legacy does it.
 *
 * Two ported constants that look like mistakes and are not:
 *
 *   TOAST_LIMIT = 1        one at a time. On a POS tablet a stack of toasts
 *                          covers the order being taken.
 *   REMOVE_DELAY  ~16 min  how long a dismissed toast stays in the array before
 *                          being dropped. Auto-dismissal is Radix's `duration`
 *                          prop, per toast — the fiskal storno warnings pass
 *                          12 s deliberately, because "kasa has no paper" must
 *                          not vanish in three.
 */

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY_MS = 1_000_000

export type ToastVariant = 'default' | 'destructive'

export interface ToastOptions {
  title?: ReactNode
  description?: ReactNode
  variant?: ToastVariant
  /** Milliseconds before Radix closes it. Omitted means the Radix default. */
  duration?: number
}

export interface ActiveToast extends ToastOptions {
  id: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ToastState {
  toasts: ActiveToast[]
}

type ToastAction =
  | { type: 'ADD'; toast: ActiveToast }
  | { type: 'UPDATE'; toast: Partial<ActiveToast> & { id: string } }
  | { type: 'DISMISS'; toastId?: string }
  | { type: 'REMOVE'; toastId?: string }

const removalTimers = new Map<string, ReturnType<typeof setTimeout>>()
const listeners: Array<(state: ToastState) => void> = []
let memoryState: ToastState = { toasts: [] }
let nextId = 0

function scheduleRemoval(toastId: string): void {
  if (removalTimers.has(toastId)) return
  removalTimers.set(
    toastId,
    setTimeout(() => {
      removalTimers.delete(toastId)
      dispatch({ type: 'REMOVE', toastId })
    }, TOAST_REMOVE_DELAY_MS),
  )
}

export function toastReducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case 'ADD':
      return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) }

    case 'UPDATE':
      return {
        ...state,
        toasts: state.toasts.map((toast) =>
          toast.id === action.toast.id ? { ...toast, ...action.toast } : toast,
        ),
      }

    case 'DISMISS': {
      const { toastId } = action
      if (toastId) scheduleRemoval(toastId)
      else for (const toast of state.toasts) scheduleRemoval(toast.id)

      return {
        ...state,
        toasts: state.toasts.map((toast) =>
          toast.id === toastId || toastId === undefined ? { ...toast, open: false } : toast,
        ),
      }
    }

    case 'REMOVE':
      if (action.toastId === undefined) return { ...state, toasts: [] }
      return { ...state, toasts: state.toasts.filter((toast) => toast.id !== action.toastId) }
  }
}

function dispatch(action: ToastAction): void {
  memoryState = toastReducer(memoryState, action)
  for (const listener of listeners) listener(memoryState)
}

export function toast(options: ToastOptions): { id: string; dismiss: () => void } {
  nextId = (nextId + 1) % Number.MAX_SAFE_INTEGER
  const id = String(nextId)

  const dismiss = () => dispatch({ type: 'DISMISS', toastId: id })

  dispatch({
    type: 'ADD',
    toast: {
      ...options,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  return { id, dismiss }
}

export function useToast() {
  const [state, setState] = useState<ToastState>(memoryState)

  useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) listeners.splice(index, 1)
    }
  }, [])

  return {
    toasts: state.toasts,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS', toastId }),
  }
}

// `resetToasts` was here and is deleted — §9.35, 2026-08-06. A test seam no test used: it cleared the
// module-level store between cases, and nothing ever needed it cleared.
//
// The state it reset is real and still module-level — `memoryState`, `nextId` and `removalTimers` all
// outlive a component — so a future test that asserts on toasts across cases will want this back.
// Writing it is four lines; keeping four unused lines that claim to be a seam is a promise to a test
// that does not exist.
